import { Radio, Tag } from 'antd';
import classNames from 'classnames';
import { find, get, isArray, values as objValues, toPairs } from 'lodash-es';
import { useContext, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { isDateRangeEqual } from '../../common/utils/dateRangeUtils';
import { FAIcon } from '../../components/adapters/fontAwesomeAdapters';
import { FiltersAndSortContext } from '../../components/data/FiltersAndSortProvider';
import { FormItemFieldRegistration } from '../../components/forms/basicFormElements';
import { FormItemDateRangePopup } from '../../components/forms/dateElements';
import { FormItem } from '../../components/forms/FormItem';
import { isFormValuePresent } from '../../components/forms/formHelpers';
import { useFormContext } from '../../components/forms/forms';
import {
  findAndPrintSelectOption,
  findSelectOption,
} from '../../components/forms/selectUtils';
import { useResponsiveQueries } from '../../components/responsive/responsiveQueries';
import {
  FilterPopup,
  MonitorFilter,
  useAirportOptions,
  useSimpleAccountOptions,
  useStatusOptions,
} from './MonitorFilterShared';
import { LocationMode, MonitorContext } from './monitorContexts';
import {
  FormItemSearchSingleSelect,
  FormItemSearchTextFilterSubmenu,
} from './monitorFiltersFormItems';
import { StatusCheckboxList } from './StatusCheckboxList';
import { useAccountElementsStyles } from '../../components/domainSpecific/accountElements.styles';
import { useMonitorSearchFiltersStyles } from './MonitorSearchFilters.styles';
import { useMonitorFilterStyles } from './MonitorFilter.styles';

function DateRangeFilter({ name, isPresent }) {
  const { formInstance, forceUpdate } = useFormContext();
  const {
    filters: { setValues: setFilters },
  } = useContext(FiltersAndSortContext);
  const { styles: sharedStyles } = useMonitorSearchFiltersStyles();
  const { styles } = useMonitorFilterStyles();

  const handleClear = e => {
    e.stopPropagation();
    formInstance.resetFields([name]);
    forceUpdate();
    setFilters(formInstance.getFieldsValue());
  };

  return (
    <div className={sharedStyles.dateRangeFilterContainer}>
      <FormItemDateRangePopup
        name={name}
        popupComponent={FilterPopup}
        placeholderId="filters.labels.dateRange"
        popupProps={{
          isPresent,
          rootClassName: sharedStyles.dateRangeFilterOverlay,
        }}
      />
      {isPresent && (
        <FAIcon
          icon="times-circle"
          className={styles.dateRangeFilterClearButton}
          onClick={handleClear}
        />
      )}
    </div>
  );
}

function AccountFilter({ name, isPresent, noPopup }) {
  const options = useSimpleAccountOptions();
  const { styles } = useAccountElementsStyles();
  const content = (
    <FormItemSearchSingleSelect
      name={name}
      options={options}
      className={styles.accountSelectDropdown}
      labelId={noPopup ? 'filters.labels.account' : undefined}
    />
  );
  if (noPopup) return content;
  return (
    <FilterPopup labelId="filters.labels.account" isPresent={isPresent}>
      {content}
    </FilterPopup>
  );
}

function StatusFilter({ name, isPresent, noPopup }) {
  const options = useStatusOptions();
  const content = (
    <FormItem
      name={name}
      noStyle
      component={StatusCheckboxList}
      options={options}
      labelId={noPopup ? 'filters.labels.status' : undefined}
    />
  );
  if (noPopup) return content;
  return (
    <FilterPopup labelId="filters.labels.status" isPresent={isPresent}>
      {content}
    </FilterPopup>
  );
}
function StatusTagContent({ value }) {
  const options = useStatusOptions();
  const text = findAndPrintSelectOption(value, options);
  return text ? (
    <span className="text" title={text}>
      {text}
    </span>
  ) : null;
}

function AirportTagContent({ value }) {
  const options = useAirportOptions();
  const selectedAirport = findSelectOption(value, options);
  return selectedAirport ? (
    <span className="text" title={selectedAirport.value}>
      {selectedAirport.value}
    </span>
  ) : null;
}

function AccountTagContent({ value }) {
  const options = useSimpleAccountOptions();
  const selectedAccount = findSelectOption(value, options);
  return selectedAccount ? (
    <span className="text" title={selectedAccount.value}>
      {selectedAccount.value}
    </span>
  ) : null;
}

const SHIPMENT_DETAILS_OPTIONS = [
  {
    name: 'destinationContactName',
    labelId: 'filters.labels.shipmentDetails.destinationContactName',
  },
  {
    name: 'destinationCity',
    labelId: 'filters.labels.shipmentDetails.destinationCity',
  },
  {
    name: 'destinationCountry',
    labelId: 'filters.labels.shipmentDetails.destinationCountry',
  },
];
function ShipmentDetailsFilter({ name, isPresent, noPopup }) {
  const content = (
    <FormItemSearchTextFilterSubmenu
      name={name}
      options={SHIPMENT_DETAILS_OPTIONS}
      labelId={noPopup ? 'filters.labels.shipmentDetails' : undefined}
    />
  );
  if (noPopup) return content;
  return (
    <FilterPopup labelId="filters.labels.shipmentDetails" isPresent={isPresent}>
      {content}
    </FilterPopup>
  );
}

function useStageOptions() {
  const intl = useIntl();
  const { data, baseCounts, activeSection, locationMode } =
    useContext(MonitorContext);

  const optionsData = useMemo(
    () =>
      toPairs(data).map(([key, group]) => {
        const labelText = intl.formatMessage({ id: `monitor.stage.${key}` });
        const baselineCount = baseCounts?.[key]?.totalRows;
        const fallbackCount = group?.totalRows || 0;
        const count = baselineCount ?? fallbackCount;
        return {
          key,
          labelText,
          count,
        };
      }),
    [data, baseCounts, intl]
  );

  const activeGroup = find(optionsData, o => o.key === activeSection);
  const stageLabelText = activeGroup?.labelText || '';
  const count = activeGroup?.count || 0;

  const locationLabel = intl.formatMessage({
    id: `monitor.locationMode.${locationMode}.short`,
  });

  return { optionsData, stageLabelText, count, locationLabel };
}

function StageFilter({ name, isPresent }) {
  const media = useResponsiveQueries();
  const isMd = media.md || media.sm || media.xs;
  const { optionsData, stageLabelText, count, locationLabel } =
    useStageOptions();
  const { locationMode, setLocationMode } = useContext(MonitorContext);
  const { styles: sharedStyles } = useMonitorSearchFiltersStyles();
  const { styles } = useMonitorFilterStyles();

  const options = useMemo(
    () =>
      optionsData.map(opt => ({
        value: opt.key,
        label: (
          <>
            <span className={sharedStyles.stageLabelText}>{opt.labelText}</span>
            <span className={sharedStyles.stageLabelCount}>({opt.count})</span>
          </>
        ),
        title: opt.labelText,
      })),
    [optionsData, sharedStyles]
  );

  const activeLabel = (
    <span className={styles.stageFilterActiveLabel}>
      {stageLabelText && `${stageLabelText} (${count})`}
      {!isMd && (
        <>
          {stageLabelText && (
            <>
              <span className="separator">|</span>{' '}
            </>
          )}
          <span className="location">{locationLabel}</span>
        </>
      )}
    </span>
  );

  return (
    <FilterPopup
      label={activeLabel}
      isPresent={isPresent}
      rootClassName={sharedStyles.stageFilterOverlay}
    >
      <div className={sharedStyles.stageFilterLocationModeHeader}>
        <Radio.Group
          value={locationMode}
          onChange={e => setLocationMode(e.target.value)}
          size="small"
        >
          <Radio value={LocationMode.destination}>
            <FormattedMessage id="monitor.locationMode.destination.short" />
          </Radio>
          <Radio value={LocationMode.origin}>
            <FormattedMessage id="monitor.locationMode.origin.short" />
          </Radio>
        </Radio.Group>
      </div>
      {options?.length > 1 && (
        <FormItemSearchSingleSelect
          name={name}
          options={options}
          formItemComponentProps={{ allowClear: false }}
        />
      )}
    </FilterPopup>
  );
}

export const MonitorFilterType = {
  DATE_RANGE: {
    name: 'dateRange',
    filterComponent: DateRangeFilter,
    isFilterEqual: isDateRangeEqual,
    isClearable: true,
    isSortable: true,
    isFilterPresent: val => val?.from || val?.to,
  },
  ACCOUNT: {
    name: 'accountNumber',
    filterComponent: AccountFilter,
    isClearable: true,
    isSortable: true,
    isAdvanced: true,
    tagContentComponent: AccountTagContent,
    unrollTags: val => (val || []).map(v => ({ key: v, value: v })),
  },
  STATUS: {
    name: 'shipmentStatus',
    filterComponent: StatusFilter,
    tagContentComponent: StatusTagContent,
    isClearable: true,
    isSortable: true,
    isAdvanced: true,
    isFilterPresent: val => val?.length > 0,
    unrollTags: val => (val || []).map(v => ({ key: v, value: v })),
  },
  ORIGIN_AIRPORT: {
    name: 'originAirport',
    tagContentComponent: AirportTagContent,
    isClearable: true,
    isSortable: true,
    isAdvanced: true,
    isFilterPresent: val => val?.length > 0,
    unrollTags: val => (val || []).map(v => ({ key: v, value: v })),
  },
  DESTINATION_AIRPORT: {
    name: 'destinationAirport',
    tagContentComponent: AirportTagContent,
    isClearable: true,
    isSortable: true,
    isAdvanced: true,
    isFilterPresent: val => val?.length > 0,
    unrollTags: val => (val || []).map(v => ({ key: v, value: v })),
  },
  SHIPMENT_DETAILS: {
    name: 'shipmentDetails',
    filterComponent: ShipmentDetailsFilter,
    isClearable: true,
    isSortable: false,
    unrollTags: val => toPairs(val).map(([key, value]) => ({ key, value })),
    isAdvanced: true,
  },
  STAGE: {
    name: 'stage',
    filterComponent: StageFilter,
    isClearable: false,
    isSortable: false,
  },
};

export function MonitorFiltersRegistration() {
  return objValues(MonitorFilterType).map(({ name }) => (
    <FormItemFieldRegistration key={name} name={name} />
  ));
}

export const DateRangeMonitorFilter = props => (
  <MonitorFilter {...props} definition={MonitorFilterType.DATE_RANGE} />
);
export const StageMonitorFilter = props => (
  <MonitorFilter {...props} definition={MonitorFilterType.STAGE} />
);

export function MonitorFilterTag({ definition }) {
  const { formInstance, forceUpdate } = useFormContext();
  const {
    filters: { values: filters, setValues: setFilters },
  } = useContext(FiltersAndSortContext);

  const {
    name,
    tagContentComponent: TCC,
    isClearable,
    isFilterPresent = isFormValuePresent,
    unrollTags,
  } = definition;

  const savedVal = get(filters, name);
  const visible = isFilterPresent(savedVal);
  const closable = isClearable;

  if (!visible) return null;

  if (
    unrollTags &&
    (isArray(savedVal) || (typeof savedVal === 'object' && savedVal !== null))
  ) {
    const tags = unrollTags(savedVal);
    return tags.map(({ key, value }) => {
      if (!isFormValuePresent(value)) return null;
      return (
        <Tag
          key={key}
          className={classNames({ closable })}
          closable={closable}
          closeIcon={<FAIcon icon="times-circle" />}
          onClose={() => {
            const nextValue = isArray(savedVal)
              ? savedVal.filter(v => v !== key)
              : { ...savedVal, [key]: undefined };
            formInstance.setFieldsValue({ [name]: nextValue });
            setFilters(formInstance.getFieldsValue());
            forceUpdate();
          }}
        >
          <TagContent TCC={TCC} value={value} />
        </Tag>
      );
    });
  }

  return (
    <Tag
      key={name}
      className={classNames({ closable })}
      closable={closable}
      closeIcon={<FAIcon icon="times-circle" />}
      onClose={() => {
        formInstance.resetFields([name]);
        forceUpdate();
        setFilters(formInstance.getFieldsValue());
      }}
    >
      <TagContent TCC={TCC} value={savedVal} />
    </Tag>
  );
}

function TagContent({ TCC, value }) {
  return TCC ? (
    <TCC value={value} />
  ) : (
    <span className="text" title={value}>
      {value}
    </span>
  );
}
